import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setUserInfo, clearUserInfo, selectIsAuthenticated, selectUserType, selectUsername, selectName, selectSportsClubName, selectTeamName, selectLogoUrl, selectTeams, selectSelectedTeamId } from '../store/userSlice';
import { AuthenticationService } from '../api/index.ts';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticatedRedux = useSelector(selectIsAuthenticated);
  const userType = useSelector(selectUserType);
  const username = useSelector(selectUsername);
  const name = useSelector(selectName);
  const sportsClubName = useSelector(selectSportsClubName);
  const teamName = useSelector(selectTeamName);
  const logoUrl = useSelector(selectLogoUrl);
  const teams = useSelector(selectTeams);
  const selectedTeamId = useSelector(selectSelectedTeamId);
  const [isInitializing, setIsInitializing] = useState(!isAuthenticatedRedux);

  // Restore auth state on page refresh using JWT cookie
  useEffect(() => {
    if (isAuthenticatedRedux) {
      setIsInitializing(false);
      return;
    }

    const restoreSession = async () => {
      // Interceptor ustawił flagę forceLogout gdy backend był niedostępny
      // i nie mógł wyczyścić cookies przez /logout
      if (sessionStorage.getItem('forceLogout')) {
        sessionStorage.removeItem('forceLogout');
        setIsInitializing(false);
        return;
      }

      try {
        const userInfo = await AuthenticationService.getLoggedUserInfo();
        if (userInfo && userInfo.userId) {
          dispatch(setUserInfo({
            userId: userInfo.userId,
            username: userInfo.username,
            name: userInfo.name,
            userType: userInfo.userType,
            roles: userInfo.roles || [],
            teams: userInfo.teams || [],
            sportsClubId: userInfo.sportsClubId,
            sportsClubName: userInfo.sportsClubName,
            logoUrl: userInfo.logoUrl,
            expressModeEnabled: userInfo.expressModeEnabled,
            expressModeThreshold: userInfo.expressModeThreshold
          }));
        }
      } catch (error) {
        // 401 = no valid JWT cookie, user is not logged in — that's fine
      } finally {
        setIsInitializing(false);
      }
    };

    restoreSession();
  }, []);

  const logout = async () => {
    try {
      await AuthenticationService.logout();
    } catch (error) {
      // Kontynuuj wylogowanie nawet jeśli endpoint zwróci błąd
    } finally {
      // Wyczyść stan Redux
      dispatch(clearUserInfo());
    }
  };

  const isAuthenticated = () => {
    return isAuthenticatedRedux;
  };

  const hasRole = (role) => {
    const hasRoleResult = userType === role;
    return hasRoleResult;
  };

  return (
    <AuthContext.Provider value={{
      logout,
      isAuthenticated,
      hasRole,
      userType,
      username,
      name,
      sportsClubName,
      teamName,
      logoUrl,
      teams,
      selectedTeamId,
      isInitializing
    }}>
      {isInitializing ? null : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
